'use client'

import { useState } from 'react'
import type { TypeProvider } from '@lib/types'
import { Button, Icon, Link, Typography, Modal, VideoPlayer } from '@shc/ui'
import useAnalytics from '@hooks/use-analytics'
import { getYoutubeVideoIdFromUrl } from '@lib/utilities/video-utilities'
import { startYouTubeTracking } from '@snowplow/browser-plugin-youtube-tracking'

export type ProviderAboutProps = {
  provider: TypeProvider
}

const ProviderAbout = ({ provider }: ProviderAboutProps) => {
  const isProviderVideo =
    Array.isArray(provider.video_urls) &&
    provider.video_urls?.length > 0 &&
    provider.video_urls[0] !== ''
  const videoId = getYoutubeVideoIdFromUrl(provider.video_urls?.[0] ?? '')

  const [isNPIOpen, setIsNPIOpen] = useState(false)
  const { track: trackSnowplow } = useAnalytics()

  return (
    <>
      <div className="flex flex-col gap-y-8">
        <Typography variant="h2">About {provider.name_formatted_with_degrees}</Typography>

        {isProviderVideo && videoId?.length && (
          <div>
            <VideoPlayer
              iframeId={videoId}
              url={provider.video_urls?.[0] ?? ''}
              className="w=full"
              data-testid="provider-video"
              onReady={() => {
                if (videoId) {
                  startYouTubeTracking({
                    id: videoId,
                    video: videoId,
                    captureEvents: ['play', 'pause', 'end', 'percent_progress', 'error'],
                  })
                } else {
                  console.error('Cannot find YouTube video ID to track analytics')
                }
              }}
            />
          </div>
        )}

        {provider.biography && <Typography>{provider.biography}</Typography>}
        <section>
          {!!provider.age_in_years && (
            <div>
              <Typography as="span" variant="body-semibold">
                Age:
              </Typography>
              &nbsp;{provider.age_in_years} <br />
            </div>
          )}
          {!!provider.year_started_with_sharp && (
            <div>
              <Typography as="span" variant="body-semibold">
                In practice since:
              </Typography>
              &nbsp;{provider.year_started_with_sharp} <br />
            </div>
          )}
          {provider.gender && (
            <div>
              <Typography as="span" variant="body-semibold">
                Gender:
              </Typography>
              &nbsp;{provider.gender} <br />
            </div>
          )}
          {provider.name_pronounciation && (
            <div>
              <Typography as="span" variant="body-semibold">
                Name pronunciation:
              </Typography>
              &nbsp;{provider.name_pronounciation} <br />
            </div>
          )}
          {provider.languages && provider.languages.length > 0 && (
            <div>
              <Typography as="span" variant="body-semibold">
                Languages:&nbsp;
              </Typography>

              {provider.languages.map((language, index) => {
                return (
                  <span key={language.id}>
                    {index > 0 && ', '}
                    {language.id}
                  </span>
                )
              })}
            </div>
          )}
        </section>

        {provider.educations && provider.educations.length > 0 && (
          <section>
            <div>
              <Typography variant="h5" as="h3">
                Education
              </Typography>
            </div>
            <div className="pt-3">
              {provider.educations.map((school, index) => {
                return (
                  <div key={`${index}-${school.school_name}`}>
                    <Typography as="span" variant="body-semibold">
                      {school.school_name}:
                    </Typography>
                    &nbsp;{school.type} <br />
                  </div>
                )
              })}
            </div>
          </section>
        )}

        {provider.hospital_affiliations && provider.hospital_affiliations.length > 0 && (
          <section>
            <div>
              <Typography variant="h5" as="h3">
                Hospital affiliation
              </Typography>
            </div>

            <div className="pt-3">
              {provider.hospital_affiliations.map((entities) => {
                return (
                  <div key={entities.id}>
                    { entities.hospital_url ? 
                        <Link
                          noUnderline
                          target="_blank"
                          href={entities.hospital_url ?? undefined}>
                          {entities.hospital_name}
                        </Link>
                      : <Typography variant='body'>{entities.hospital_name}</Typography>
                    }
                  </div>
                )
              })}
            </div>
          </section>
        )}
        { provider.national_provider_id && (
          <section className="flex flex-col gap-y-2 items-start">
            <div className="flex items-center gap-x-1">
              <Typography variant="h5" as="h3">
                NPI
              </Typography>
              <Button
                color="transparent"
                shape="circle"
                size="xs"
                onClick={() => {
                  setIsNPIOpen(true)
                  trackSnowplow({
                    event: { name: 'modal_open', data: {} },
                    contexts: [{ name: 'modal', data: { modal_name: 'NpiModal' } }],
                  })
                }}
                aria-label="National Provider Identifier information"
                data-testid="npi-modal-link">
                <Icon className="h-4 text" icon="circle-info" />
              </Button>
            </div>
            <Typography>{provider.national_provider_id}</Typography>
          </section>
        )}
      </div>

      {/* NPI MODAL */}
      <Modal
        width="sm"
        open={isNPIOpen}
        onClose={() => {
          setIsNPIOpen(false)
          trackSnowplow({
            event: { name: 'modal_close', data: {} },
            contexts: [{ name: 'modal', data: { modal_name: 'NpiModal' } }],
          })
        }}
        data-testid="npi-modal">
        <div className="flex flex-col text-center">
          <Icon className="h-10 text-center text-primary pb-8" icon="circle-info" />
          <Typography>
            A National Provider Identifier (NPI) is a unique identification number assigned to
            individual U.S. health care providers.
          </Typography>
        </div>
      </Modal>
    </>
  )
}

export default ProviderAbout
